// Konami arcade — beat-coupled techflex round with ghost-replay loop.
//
// Cursor sweep is the primary verb: as you move the pointer (or finger),
// the segment between your previous and current sample is tested against
// every alive enemy's bounding box. Hits ring the FM bell (worklet, via
// the existing cursor pressure-edge channel) and push a shader hit impulse
// (WebGL feedback shader). Click/tap is the accessibility fallback — same
// `killEnemy()` code path.
//
// Spawn cadence is locked to the audio clock when audio is armed; spawn
// density is biased by daily `agitation`. Spawns and color picks use a
// per-round seeded PRNG (mulberry32) so a (cycle, nonce) pair fully
// determines the enemy field — a friend opening a `#arcade=...` URL plays
// the SAME field your ghost did, against your exact ghost ribbon.
//
// At round end:
//   - HUD swaps for a minimal summary (score + max combo + ghost ribbon)
//     via View Transition.
//   - `[c]` copies a challenge URL containing the deterministic seed +
//     packed kill events. URL <300 chars; DM/Twitter-shareable.
//   - `[s]` reuses the existing freeze-share pipeline.
//   - `[Enter]` replays.
//   - `[Esc]` exits.
//
// Photosensitivity (WCAG 2.3.1): every kill gets a LOCAL ring impulse via
// pushHitImpulse (already reduced-motion-no-op at the renderer). Combo ×5
// milestones additionally request a full-viewport screen flash via
// pushScreenFlash, which the renderer rate-limits to max 2 per 1000ms and
// hard-caps at strength 0.18. Reduced-motion is a no-op for the flash too.

import type { Parameters } from '../lib/parameters-schema';
import type { AudioReactor } from '../audio/reactor';
import type { GlitchRenderer } from '../renderers/webgl2';
import type { FreezeShare } from '../share/freeze';
import { mulberry32, seedForRound, randomNonce, type Rng } from './rng';
import {
  ReplayRecorder,
  renderGhostSvg,
  buildChallengeUrl,
  readChallengeFromUrl,
  type Replay,
} from './replay';

const SCORE_BASE = 10;
const MASTER_DUCKED = 0.32;
const MASTER_DEFAULT = 0.42;
const COMBO_MAX_INTENSITY_BUMP = 12; // intensity caps at 1.0 + 12*0.04 = 1.48
const COMBO_INTENSITY_PER = 0.04;
const ARCADE_BASE_INTENSITY = 1.0;
const MILESTONE_INTERVAL = 5; // ×5, ×10, ×15... get extra punctuation

interface Enemy {
  el: HTMLElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  alive: boolean;
}

export interface ArcadeHandle {
  start(): void;
  stop(): void;
  isRunning(): boolean;
}

export interface ArcadeDeps {
  // Renderer surfaces consumed by the arcade. setArcadeIntensity drives the
  // ramp; pushScreenFlash is rate-limited at the renderer (safety budget);
  // pushHitImpulse remains the per-kill local ring channel.
  renderer: Pick<GlitchRenderer, 'pushHitImpulse' | 'pushScreenFlash' | 'setArcadeIntensity'>;
  audio: AudioReactor;
  freezeShare: FreezeShare;
  // Callable so the binding stays live across main.ts's loadParameters()
  // reassignment of the per-visitor seed (see main.ts:151).
  visitorSeed: () => { raw: number };
}

export function createArcade(getParameters: () => Parameters, deps: ArcadeDeps): ArcadeHandle {
  ensureStyles();

  let running = false;
  let stage: HTMLElement | null = null;
  let timerEl: HTMLElement | null = null;
  let countEl: HTMLElement | null = null;
  let comboEl: HTMLElement | null = null;
  let enemies: Enemy[] = [];
  let score = 0;
  let raf = 0;
  let endsAt = 0;
  let perfStart = 0;
  let lastSpawnBeat = -1;

  // 2-sample cursor trail: prev → current = one segment per RAF. pointermove
  // also tests sub-segments synchronously via getCoalescedEvents() for fast
  // sweeps (native browser primitive for high-resolution pointer trails).
  let prevSample: { x: number; y: number } | null = null;
  let lastCursor: { x: number; y: number } = {
    x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
  };

  // Combo state
  let combo = 0;
  let maxCombo = 0;
  let lastHitMs = 0;

  // Replay / challenge state. pendingChallenge is read once at module load
  // (NOT here — at the call to readChallengeFromUrl below) so a friend's
  // URL hash activates next time arcade starts. The URL hash IS the source
  // of truth; navigating away or pasting a different URL is the exit.
  let rng: Rng = Math.random;
  let roundNonce = 0;
  let recorder: ReplayRecorder | null = null;

  const start = (): void => {
    if (running) return;
    runWithViewTransition(() => actuallyStart());
  };

  const renderHud = (_params: Parameters): string => `
    <div class="herm-arcade-hud">
      <span class="herm-arcade-time" data-time></span>
      <span class="herm-arcade-combo" data-combo></span>
      <span class="herm-arcade-score" data-score>000</span>
      <button class="herm-arcade-exit" type="button" aria-label="Exit arcade">esc</button>
    </div>
  `;

  const wireHud = (): void => {
    if (!stage) return;
    timerEl = stage.querySelector('[data-time]');
    countEl = stage.querySelector('[data-score]');
    comboEl = stage.querySelector('[data-combo]');
    const exitBtn = stage.querySelector<HTMLButtonElement>('.herm-arcade-exit');
    exitBtn?.addEventListener('click', () => stop());
  };

  const beginRound = (params: Parameters): void => {
    score = 0;
    enemies = [];
    combo = 0;
    maxCombo = 0;
    lastHitMs = 0;
    lastSpawnBeat = -1;
    prevSample = null;

    // Challenge mode: if URL hash provides a replay matching today's cycle,
    // reuse its nonce so spawns reproduce exactly. Otherwise fresh nonce.
    const challenge = pendingChallenge && pendingChallenge.cycle === params.cycle
      ? pendingChallenge
      : null;
    roundNonce = challenge ? challenge.nonce : randomNonce();
    const seed = seedForRound(deps.visitorSeed().raw, params.cycle, roundNonce);
    rng = mulberry32(seed);
    recorder = new ReplayRecorder();

    const beatMs = 60_000 / params.beat_bpm;
    const barMs = beatMs * 4;
    const targetBars = Math.max(6, Math.min(8, Math.round(30000 / barMs)));
    const roundDurationMs = Math.max(24000, Math.min(32000, targetBars * barMs));
    perfStart = performance.now();
    endsAt = perfStart + roundDurationMs;

    deps.audio.setMaster(MASTER_DUCKED);
    deps.renderer.setArcadeIntensity(ARCADE_BASE_INTENSITY);
    document.addEventListener('keydown', onKeyDown);
    running = true;
    raf = requestAnimationFrame(tick);
  };

  const actuallyStart = (): void => {
    const params = getParameters();
    stage = document.createElement('div');
    stage.className = 'herm-arcade';
    stage.innerHTML = renderHud(params);
    document.body.appendChild(stage);
    wireHud();
    stage.addEventListener('click', onStageClick);
    stage.addEventListener('pointermove', onPointerMove);

    // Challenge mode — render the ghost ribbon during play so the player can
    // see the path they're racing. Uses the same SVG renderer as the summary,
    // animation duration matches a typical round (CSS controls the trace).
    if (pendingChallenge && pendingChallenge.cycle === params.cycle) {
      const ghostHtml = renderGhostSvg(pendingChallenge.kills, 30000);
      if (ghostHtml) stage.insertAdjacentHTML('afterbegin', ghostHtml);
    }

    beginRound(params);
  };

  const replay = (): void => {
    document.removeEventListener('keydown', onSummaryKeyDown);
    if (!stage) { actuallyStart(); return; }
    const params = getParameters();
    runWithViewTransition(() => {
      if (!stage) return;
      stage.innerHTML = renderHud(params);
      wireHud();
      // Re-render the ghost ribbon for the next round if still in challenge mode
      if (pendingChallenge && pendingChallenge.cycle === params.cycle) {
        const ghostHtml = renderGhostSvg(pendingChallenge.kills, 30000);
        if (ghostHtml) stage.insertAdjacentHTML('afterbegin', ghostHtml);
      }
    });
    beginRound(params);
  };

  const stop = (): void => {
    if (!stage) return;
    running = false;
    cancelAnimationFrame(raf);
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keydown', onSummaryKeyDown);
    deps.audio.setMaster(MASTER_DEFAULT);
    deps.renderer.setArcadeIntensity(0);
    runWithViewTransition(() => {
      stage?.remove();
      stage = null; timerEl = null; countEl = null; comboEl = null;
      enemies = [];
    });
  };

  const onStageClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    const enemyEl = target?.closest('.herm-enemy');
    const enemy = enemies.find((en) => en.el === enemyEl);
    if (enemy?.alive) {
      const r = enemy.el.getBoundingClientRect();
      killEnemy(enemy, r.left + r.width / 2, r.top + r.height / 2);
    }
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (!running) return;
    lastCursor = { x: e.clientX, y: e.clientY };
    type CoalescedReadable = PointerEvent & { getCoalescedEvents?: () => PointerEvent[] };
    const ce = e as CoalescedReadable;
    const events = (typeof ce.getCoalescedEvents === 'function' ? ce.getCoalescedEvents() : null) ?? [e];
    for (const sub of events) {
      const cur = { x: sub.clientX, y: sub.clientY };
      if (prevSample) testSegmentAgainstAllEnemies(prevSample, cur);
      prevSample = cur;
    }
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); stop(); }
  };

  const onSummaryKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      announce('new round starting');
      replay();
      return;
    }
    if ((e.key === 's' || e.key === 'S') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      announce('opening share sheet');
      void deps.freezeShare.trigger();
      return;
    }
    if ((e.key === 'c' || e.key === 'C') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      void copyChallengeUrl().then(() => announce('challenge URL copied'));
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      announce('exited arcade');
      stop();
    }
  };

  const tick = (now: number): void => {
    if (!running || !stage) return;
    const remaining = Math.max(0, endsAt - now);
    if (timerEl) timerEl.textContent = `${(remaining / 1000).toFixed(1)}s`;
    if (remaining <= 0) { renderSummary(); return; }

    const params = getParameters();
    const beatMs = 60_000 / params.beat_bpm;
    const clock = deps.audio.getClock();
    const elapsedMs = clock
      ? (clock.currentTime - clock.epochTime) * 1000
      : now - perfStart;
    const beatIndex = Math.floor(elapsedMs / beatMs);
    if (beatIndex > lastSpawnBeat) {
      const spawnChance = 0.3 + 0.6 * params.agitation;
      if (rng() < spawnChance) spawnEnemy(params);
      lastSpawnBeat = beatIndex;
    }

    // Combo break: silent shader dip + intensity reset to baseline
    if (combo >= 1 && now - lastHitMs > comboWindowMs(params)) {
      if (combo >= 4) {
        const uvX = lastCursor.x / window.innerWidth;
        const uvY = 1 - lastCursor.y / window.innerHeight;
        deps.renderer.pushHitImpulse(uvX, uvY, 0.2);
      }
      combo = 0;
      deps.renderer.setArcadeIntensity(ARCADE_BASE_INTENSITY);
      updateComboHud();
    }

    if (prevSample) {
      testSegmentAgainstAllEnemies(prevSample, lastCursor);
    }
    prevSample = lastCursor;

    for (const en of enemies) {
      if (!en.alive) continue;
      en.x += en.vx;
      en.y += en.vy;
      en.vy += 0.04;
      en.el.style.transform = `translate(${en.x}px, ${en.y}px) rotate(${en.x * 0.4}deg)`;
      if (en.y > window.innerHeight + 60 || en.x < -200 || en.x > window.innerWidth + 200) {
        en.alive = false;
        en.el.remove();
      }
    }
    enemies = enemies.filter((en) => en.alive);

    raf = requestAnimationFrame(tick);
  };

  const testSegmentAgainstAllEnemies = (a: { x: number; y: number }, b: { x: number; y: number }): void => {
    for (const en of enemies) {
      if (!en.alive) continue;
      const r = en.el.getBoundingClientRect();
      if (segmentIntersectsRect(a, b, r)) {
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        killEnemy(en, cx, cy);
      }
    }
  };

  const killEnemy = (en: Enemy, x: number, y: number): void => {
    if (!en.alive) return;
    en.alive = false;
    en.el.classList.add('herm-enemy--dead');
    setTimeout(() => en.el.remove(), 350);

    const uvX = Math.max(0, Math.min(1, x / window.innerWidth));
    const uvY = Math.max(0, Math.min(1, 1 - y / window.innerHeight));
    // Bell pluck: pressure rising-edge through worklet's existing cursor channel
    deps.audio.setCursor(uvX, uvY, 0);
    deps.audio.setCursor(uvX, uvY, 0.85 + 0.15 * comboFraction());
    // Local hit ring on EVERY kill — renderer guards on prefers-reduced-motion
    deps.renderer.pushHitImpulse(uvX, uvY, 0.6 + 0.4 * comboFraction());

    score += SCORE_BASE * multiplier();
    bumpCombo();
    if (countEl) countEl.textContent = String(score).padStart(3, '0');

    // Replay sample — stored in viewport-normalized coords (0,0 = top-left)
    // so they match SVG viewBox at render time.
    recorder?.recordKill(uvX, 1 - uvY, combo);
  };

  const bumpCombo = (): void => {
    const now = performance.now();
    const params = getParameters();
    if (now - lastHitMs <= comboWindowMs(params)) combo += 1;
    else combo = 1;
    lastHitMs = now;
    if (combo > maxCombo) maxCombo = combo;
    updateComboHud();

    // Combo escalates the world. Capped at 12 → intensity 1.48.
    const intensity = ARCADE_BASE_INTENSITY
      + Math.min(combo, COMBO_MAX_INTENSITY_BUMP) * COMBO_INTENSITY_PER;
    deps.renderer.setArcadeIntensity(intensity);

    // ×5 milestones get extra punctuation: local-strong impulse + budgeted
    // screen flash. The renderer rate-limits the flash; safe to call always.
    if (combo > 0 && combo % MILESTONE_INTERVAL === 0) {
      const uvX = lastCursor.x / window.innerWidth;
      const uvY = 1 - lastCursor.y / window.innerHeight;
      deps.renderer.pushHitImpulse(uvX, uvY, 1.0);
      deps.renderer.pushScreenFlash(Math.min(0.18, 0.10 + 0.08 * comboFraction()));
    }
  };

  const comboWindowMs = (p: Parameters): number => {
    const bm = 60_000 / p.beat_bpm;
    return Math.max(450, Math.min(900, bm * 1.25));
  };
  const multiplier = (): number => Math.min(5, 1 + Math.floor(combo / 4));
  const comboFraction = (): number => Math.min(1, combo / 12);

  const updateComboHud = (): void => {
    if (!comboEl) return;
    comboEl.textContent = combo > 1 ? `×${combo}` : '';
  };

  const spawnEnemy = (params: Parameters): void => {
    if (!stage) return;
    // Abstract palette-radial-gradient circle. currentColor + radial-gradient
    // means a single inline `style.color` drives both gradient AND glow —
    // single source of truth, no string-templated CSS gradients.
    const el = document.createElement('button');
    el.className = 'herm-enemy';
    el.type = 'button';
    el.setAttribute('aria-label', 'enemy');
    const palette = params.palette;
    const color = palette[Math.floor(rng() * palette.length)] ?? '#ff00aa';
    el.style.color = color;
    // BPM-locked pulse via inline CSS variable (CSS reads var(--herm-beat-ms))
    const beatMs = 60_000 / params.beat_bpm;
    el.style.setProperty('--herm-beat-ms', `${beatMs.toFixed(0)}ms`);
    stage.appendChild(el);
    const fromLeft = rng() < 0.5;
    const x = fromLeft ? -160 : window.innerWidth + 60;
    const y = 60 + rng() * (window.innerHeight - 220);
    const vx = (fromLeft ? 1 : -1) * (1.4 + rng() * 1.6 + params.agitation * 1.5);
    const vy = -0.8 - rng() * 0.5;
    enemies.push({ el, x, y, vx, vy, alive: true });
  };

  const buildReplay = (params: Parameters): Replay => ({
    version: 1,
    cycle: params.cycle,
    nonce: roundNonce,
    score,
    kills: recorder?.kills ?? [],
  });

  const copyChallengeUrl = async (): Promise<void> => {
    if (!stage) return;
    const replayPayload = (stage as HTMLElement & { __replay?: Replay }).__replay;
    if (!replayPayload) return;
    const url = buildChallengeUrl(replayPayload);
    // Inline feedback at the point of interaction — swap the button's own
    // label for ~1.5s. Scoping the swap to the button (vs the whole row)
    // keeps neighbouring buttons readable and matches the inline-feedback
    // rule's per-control-state contract.
    const copyButton = stage.querySelector<HTMLButtonElement>('button[data-action="copy"]');
    const original = copyButton?.textContent ?? '[c] copy challenge';
    try {
      await navigator.clipboard.writeText(url);
      if (copyButton) {
        copyButton.textContent = '[c] copied ✓';
        setTimeout(() => {
          if (copyButton.textContent?.startsWith('[c] copied')) copyButton.textContent = original;
        }, 1500);
      }
    } catch (err) {
      // Modern browsers including iOS Safari 16.4+ support clipboard.writeText
      // in user-gesture context. No textarea/execCommand fallback for first
      // ship — surface URL in console for debug; add fallback only if reports.
      console.warn('[arcade] clipboard write failed; URL:', url, err);
      if (copyButton) {
        copyButton.textContent = '[c] copy failed';
        setTimeout(() => { copyButton.textContent = original; }, 2500);
      }
    }
  };

  const wireSummaryActions = (): void => {
    if (!stage) return;
    const get = (action: string): HTMLButtonElement | null =>
      stage!.querySelector<HTMLButtonElement>(`button[data-action="${action}"]`);

    get('copy')?.addEventListener('click', async () => {
      await copyChallengeUrl();
      announce('challenge URL copied');
    });
    get('share')?.addEventListener('click', async () => {
      // Announce BEFORE share() — iOS Share Sheet may steal focus and the
      // announcement queue gets interrupted otherwise.
      announce('opening share sheet');
      await deps.freezeShare.trigger();
    });
    get('replay')?.addEventListener('click', () => {
      announce('new round starting');
      replay();
    });
    get('exit')?.addEventListener('click', () => {
      announce('exited arcade');
      stop();
    });
  };

  const renderSummary = (): void => {
    cancelAnimationFrame(raf);
    document.removeEventListener('keydown', onKeyDown);
    deps.renderer.setArcadeIntensity(0);
    // Strip remaining enemies before snapshot so they don't appear in summary
    for (const en of enemies) en.el.remove();
    enemies = [];

    const params = getParameters();
    const replayPayload = buildReplay(params);
    const ghostSvg = renderGhostSvg(replayPayload.kills, 3000);
    const challengeChip = pendingChallenge
      ? `<div class="herm-arcade-summary-vs">vs ghost · ${String(pendingChallenge.score).padStart(3, '0')}</div>`
      : '';

    runWithViewTransition(
      () => {
        if (!stage) return;
        stage.innerHTML = `
          ${ghostSvg}
          <div class="herm-arcade-summary"
               role="region"
               tabindex="-1"
               aria-labelledby="herm-arcade-summary-title"
               aria-describedby="herm-arcade-summary-score herm-arcade-summary-meta">
            <div id="herm-arcade-summary-title" class="herm-arcade-summary-title">[hrmntr://arcade]</div>
            <div id="herm-arcade-summary-score" class="herm-arcade-summary-score" data-score>${String(score).padStart(3, '0')}</div>
            <div id="herm-arcade-summary-meta" class="herm-arcade-summary-meta">max combo ×${maxCombo}</div>
            ${challengeChip}
            <div class="herm-arcade-summary-actions" data-actions>
              <button data-action="copy"   type="button" aria-label="copy challenge URL">[c] copy challenge</button>
              <button data-action="share"  type="button" aria-label="share current frame">[s] share frame</button>
              <button data-action="replay" type="button" aria-label="replay round">[enter] replay</button>
              <button data-action="exit"   type="button" aria-label="exit arcade">[esc] exit</button>
            </div>
          </div>
        `;
        // Stash payload for the [c] handler so it doesn't have to rebuild.
        (stage as HTMLElement & { __replay?: Replay }).__replay = replayPayload;
        // Bind clicks before the focus call lands; buttons must be live the
        // moment the new DOM is in place.
        wireSummaryActions();
      },
      // After the view transition finishes, focus the summary REGION (not the
      // first button). Screen readers then announce title + score + max-combo
      // via aria-labelledby/describedby BEFORE the user tabs into the actions.
      () => stage?.querySelector<HTMLElement>('.herm-arcade-summary')?.focus({ preventScroll: true }),
    );

    document.addEventListener('keydown', onSummaryKeyDown);
  };

  // Smoke-harness debug surface. Production code never reads this; tests use
  // it to inspect closure state (recorder, pendingChallenge) and to force
  // round end without waiting 24-32s. Total surface: 3 closures, no behavior.
  if (typeof window !== 'undefined') {
    (window as Window & { __arcade?: ArcadeDebugHook }).__arcade = {
      recorder: () => recorder,
      pendingChallenge: () => pendingChallenge,
      forceRoundEnd: () => {
        if (running) endsAt = performance.now() - 1;
      },
    };
  }

  return { start, stop, isRunning: () => running };
}

interface ArcadeDebugHook {
  recorder: () => ReplayRecorder | null;
  pendingChallenge: () => Replay | null;
  forceRoundEnd: () => void;
}

// Module-level: read challenge from URL once at module init. The URL hash IS
// the source of truth — refreshing or pasting a different URL is the exit.
const pendingChallenge: Replay | null = readChallengeFromUrl();

// Segment-vs-rect intersection: bbox cull, endpoint-inside, then 4 segment-vs-edge tests.
function segmentIntersectsRect(a: { x: number; y: number }, b: { x: number; y: number }, r: DOMRect): boolean {
  const minX = Math.min(a.x, b.x), maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y), maxY = Math.max(a.y, b.y);
  if (maxX < r.left || minX > r.right || maxY < r.top || minY > r.bottom) return false;
  if (a.x >= r.left && a.x <= r.right && a.y >= r.top && a.y <= r.bottom) return true;
  if (b.x >= r.left && b.x <= r.right && b.y >= r.top && b.y <= r.bottom) return true;
  const tl = { x: r.left, y: r.top };
  const tr = { x: r.right, y: r.top };
  const br = { x: r.right, y: r.bottom };
  const bl = { x: r.left, y: r.bottom };
  return (
    segIntersectsSeg(a, b, tl, tr) ||
    segIntersectsSeg(a, b, tr, br) ||
    segIntersectsSeg(a, b, br, bl) ||
    segIntersectsSeg(a, b, bl, tl)
  );
}

function segIntersectsSeg(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  p4: { x: number; y: number },
): boolean {
  const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
  const denom = d1x * d2y - d1y * d2x;
  if (denom === 0) return false;
  const sx = p3.x - p1.x, sy = p3.y - p1.y;
  const t = (sx * d2y - sy * d2x) / denom;
  const u = (sx * d1y - sy * d1x) / denom;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

// View Transition API method on Document loses its `this` binding when
// extracted to a local. Always call it as a method on `document` directly.
//
// `afterFinished` runs after the transition's animation completes (or on the
// next frame when the API is unsupported / throws). Internalizing the
// lifecycle keeps callers from having to handle the VT-vs-non-VT branch.
function runWithViewTransition(cb: () => void, afterFinished?: () => void): void {
  const doc = document as unknown as {
    startViewTransition?: (callback: () => void) => { finished: Promise<void> };
  };
  if (typeof doc.startViewTransition === 'function') {
    try {
      const vt = doc.startViewTransition(cb);
      if (afterFinished) vt.finished.then(afterFinished, afterFinished);
      return;
    } catch {
      // fall through to direct call
    }
  }
  cb();
  if (afterFinished) requestAnimationFrame(afterFinished);
}

// Module-level live region for screen-reader announcements that must survive
// the summary's tear-down (replay/exit destroy the summary container, so a
// summary-local span would be torn out before the AT polls it). One node,
// appended to <body>, reused across all four summary actions.
const globalAnnouncer: HTMLElement | null = (() => {
  if (typeof document === 'undefined') return null;
  const el = document.createElement('span');
  el.className = 'herm-sr-only';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-atomic', 'true');
  el.dataset.hermGlobalStatus = 'true';
  // body may not exist yet if this module is imported very early; defer.
  if (document.body) document.body.appendChild(el);
  else document.addEventListener('DOMContentLoaded', () => document.body.appendChild(el), { once: true });
  return el;
})();

function announce(text: string): void {
  if (!globalAnnouncer) return;
  // Clear then set on next frame so repeated text re-announces (live regions
  // only emit on textContent change).
  globalAnnouncer.textContent = '';
  requestAnimationFrame(() => {
    globalAnnouncer.textContent = text;
  });
}

let styled = false;
function ensureStyles(): void {
  if (styled || typeof document === 'undefined') return;
  const css = `
.herm-arcade {
  position: fixed; inset: 0;
  z-index: 200;
  /* No background tint — the shader's uArcadeIntensity-driven vignette + mosh
     boost + palette intensify do the visual mode shift. A second tint on top
     would muddy the palette. */
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  pointer-events: auto;
}
.herm-arcade-hud {
  position: absolute;
  top: clamp(12px, 2vw, 20px);
  left: clamp(12px, 2vw, 20px);
  right: clamp(12px, 2vw, 20px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-size: clamp(0.8rem, 1vw, 0.95rem);
  letter-spacing: 0.16em;
  color: color-mix(in oklch, var(--herm-pal-b, white) 30%, white 70%);
  text-shadow: 0 0 6px color-mix(in oklch, var(--herm-pal-a, magenta) 50%, transparent);
  z-index: 2;
}
.herm-arcade-time, .herm-arcade-score, .herm-arcade-combo {
  font-variant-numeric: tabular-nums;
}
.herm-arcade-combo {
  color: var(--herm-pal-b, #00ffe0);
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  min-width: 3em;
  text-align: right;
}
.herm-arcade-score {
  font-size: clamp(1.4rem, 2.4vw, 2rem);
  view-transition-name: arcade-score;
}
.herm-arcade-exit {
  background: transparent;
  border: 1px solid var(--herm-stroke, rgba(255,255,255,0.4));
  color: inherit;
  padding: 0.3em 0.7em;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  letter-spacing: 0.16em;
}
/* Abstract circle enemy — currentColor drives both radial-gradient AND glow.
   BPM-locked pulse via --herm-beat-ms set inline per spawn. */
.herm-enemy {
  position: absolute;
  top: 0; left: 0;
  width: clamp(56px, 7vw, 84px);
  height: clamp(56px, 7vw, 84px);
  border-radius: 50%;
  border: 0;
  padding: 0;
  cursor: crosshair;
  user-select: none;
  background: radial-gradient(
    circle at center,
    color-mix(in oklch, currentColor 90%, white 10%) 0%,
    color-mix(in oklch, currentColor 60%, transparent) 35%,
    transparent 70%
  );
  box-shadow:
    0 0 24px currentColor,
    0 0 8px color-mix(in oklch, currentColor 80%, white 20%);
  transition: transform 200ms ease-out, opacity 350ms ease-out, box-shadow 200ms ease-out;
  will-change: transform, opacity;
  animation: herm-enemy-pulse var(--herm-beat-ms, 600ms) ease-in-out infinite alternate;
  z-index: 2;
}
.herm-enemy:hover {
  filter: brightness(1.4);
  box-shadow:
    0 0 36px currentColor,
    0 0 14px color-mix(in oklch, currentColor 90%, white 10%);
}
.herm-enemy--dead {
  opacity: 0;
  transform: scale(2.4) !important;
  pointer-events: none;
  animation: none;
}
@keyframes herm-enemy-pulse {
  from { transform: scale(0.92); }
  to   { transform: scale(1.08); }
}
.herm-arcade-summary {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: color-mix(in oklch, var(--herm-pal-b, white) 30%, white 70%);
  text-shadow: 0 0 6px color-mix(in oklch, var(--herm-pal-a, magenta) 50%, transparent);
  z-index: 2;
}
.herm-arcade-summary-title {
  font-size: clamp(0.9rem, 1.2vw, 1.1rem);
  letter-spacing: 0.18em;
  view-transition-name: arcade-title;
}
.herm-arcade-summary-score {
  font-size: clamp(3.5rem, 8vw, 6rem);
  font-variant-numeric: tabular-nums;
  view-transition-name: arcade-score;
  letter-spacing: 0.05em;
}
.herm-arcade-summary-meta {
  font-size: clamp(0.85rem, 1.2vw, 1rem);
  letter-spacing: 0.2em;
  opacity: 0.85;
}
.herm-arcade-summary-vs {
  font-size: clamp(0.85rem, 1.2vw, 1rem);
  letter-spacing: 0.18em;
  opacity: 0.75;
  color: color-mix(in oklch, var(--herm-pal-b, #00ffe0) 60%, white 40%);
}
.herm-arcade-summary-actions {
  margin-top: 1.2rem;
  font-size: clamp(0.75rem, 1vw, 0.9rem);
  letter-spacing: 0.18em;
  opacity: 0.85;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.8rem;
  justify-content: center;
  align-items: center;
}
/* Summary action buttons. Keep the bracket-prefix typography for sighted
   users; aria-label on each <button> gives screen readers a clean label
   without the bracket noise. 44px min-height = WCAG 2.2 AAA touch target. */
.herm-arcade button[data-action] {
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  cursor: pointer;
  min-height: 44px;
  padding: 0 0.6em;
  border-radius: 4px;
  /* Kill the 300ms double-tap delay on iOS Safari for the action row. */
  touch-action: manipulation;
}
.herm-arcade button[data-action]:hover,
.herm-arcade button[data-action]:focus-visible {
  outline: 1px dashed currentColor;
  outline-offset: 2px;
  opacity: 1;
}
/* Region focus is for SR/keyboard parity — quiet outline on focus-visible,
   no visual on plain focus (mouse-driven) so the hop into the region from
   the View Transition doesn't paint a ring on click users. */
.herm-arcade-summary[tabindex="-1"]:focus { outline: none; }
.herm-arcade-summary[tabindex="-1"]:focus-visible {
  outline: 1px dashed currentColor;
  outline-offset: 6px;
  border-radius: 6px;
}
/* Visually-hidden helper for the global SR announcer (mounted under <body>
   in module init). Standard WCAG-friendly recipe — clipped, off-flow, kept
   in the accessibility tree. */
.herm-sr-only {
  position: absolute;
  width: 1px; height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
/* Ghost ribbon — SVG path with normalized pathLength="1" + dashoffset
   animation. screen blend mode brightens over the dark canvas without
   needing any DOM compositing tricks. */
.herm-arcade-ghost {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.55;
  mix-blend-mode: screen;
  z-index: 1;
}
.herm-ghost-path {
  fill: none;
  stroke: var(--herm-pal-b, #00ffe0);
  stroke-width: 0.005;
  vector-effect: non-scaling-stroke;
  stroke-linejoin: round;
  stroke-linecap: round;
  filter: drop-shadow(0 0 6px var(--herm-pal-a, magenta));
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: herm-ghost-trace 3000ms cubic-bezier(.2,.8,.1,1) infinite;
}
@keyframes herm-ghost-trace {
  0%   { stroke-dashoffset: 1; }
  60%  { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 0; opacity: 0.55; }
}
::view-transition-old(arcade-score) {
  animation: herm-arcade-score-out 220ms ease both;
}
::view-transition-new(arcade-score) {
  animation: herm-arcade-score-in 420ms cubic-bezier(.2,.8,.1,1) both;
}
@keyframes herm-arcade-score-out {
  to { opacity: 0; transform: scale(0.8); }
}
@keyframes herm-arcade-score-in {
  from { opacity: 0; transform: scale(1.4); filter: blur(8px); }
  to { opacity: 1; transform: scale(1); filter: blur(0); }
}
@media (prefers-reduced-motion: reduce) {
  .herm-arcade-hud, .herm-enemy { transition-duration: 100ms; }
  .herm-enemy { animation: none; }
  .herm-ghost-path { animation: none; stroke-dashoffset: 0; }
  ::view-transition-old(arcade-score), ::view-transition-new(arcade-score) {
    animation-duration: 100ms;
  }
}
::view-transition-old(root), ::view-transition-new(root) {
  animation-duration: 0.45s;
}
`;
  const s = document.createElement('style');
  s.id = 'herminator-arcade-styles';
  s.textContent = css;
  document.head.appendChild(s);
  styled = true;
}
